/**
 * Dialog for adding/editing WhatsApp Business API credentials
 */
import { useState, useEffect } from 'react';
import { whatsappApi } from '../../services/api';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  TextField,
  Button,
  Alert,
  Box,
  Typography,
  InputAdornment,
  IconButton,
  CircularProgress,
  Grid,
  Switch,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import { 
  Visibility as Eye, 
  VisibilityOff as EyeOff, 
  Warning as AlertCircle, 
  CheckCircle as CheckCircle2,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import { useNotification } from '../../contexts/NotificationContext';
import { getTestCredentials, isDevelopment } from '../../constants/whatsapp-test-credentials';

interface WhatsAppCredentials {
  id?: string;
  business_account_id: string;
  phone_number_id: string;
  phone_number: string;
  access_token: string;
  app_id: string;
  app_secret: string;
  webhook_verify_token?: string;
  is_active_assignment?: boolean;
}

interface WhatsAppCredentialsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  smbId: string;
  existingCredentials?: WhatsAppCredentials | null;
  onSuccess?: () => void;
}

export function WhatsAppCredentialsDialog({
  open,
  onOpenChange,
  smbId,
  existingCredentials,
  onSuccess
}: WhatsAppCredentialsDialogProps) {
  const [formData, setFormData] = useState<WhatsAppCredentials>({
    business_account_id: '',
    phone_number_id: '',
    phone_number: '',
    access_token: '',
    app_id: '',
    app_secret: '',
    webhook_verify_token: '',
    is_active_assignment: true,
  });
  
  const [showTokens, setShowTokens] = useState({
    access_token: false,
    app_secret: false
  });
  
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    status: 'success' | 'error' | null;
    message: string;
  }>({ status: null, message: '' });
  const [hasExistingCredentials, setHasExistingCredentials] = useState(false);
  const [accessOnlyMode, setAccessOnlyMode] = useState(true);

  const { showNotification } = useNotification();

  const hasStoredCredentials = Boolean(existingCredentials) || hasExistingCredentials;
  const isEditing = hasStoredCredentials;
  const canUseSavedCredentials = isEditing && hasStoredCredentials && !formData.access_token;

  useEffect(() => {
    if (existingCredentials) {
      setFormData({
        ...existingCredentials,
        access_token: '',
        app_secret: '********',
        is_active_assignment: existingCredentials.is_active_assignment ?? true,
      });
      setHasExistingCredentials(true);
      setAccessOnlyMode(true);
    } else {
      setFormData({
        business_account_id: '',
        phone_number_id: '',
        phone_number: '',
        access_token: '',
        app_id: '',
        app_secret: '',
        webhook_verify_token: '',
        is_active_assignment: true,
      });
      setHasExistingCredentials(false);
      setAccessOnlyMode(false);
    }
    setTestResult({ status: null, message: '' });
  }, [existingCredentials, open]);

  // Check if credentials exist when dialog opens
  useEffect(() => {
    if (open && !existingCredentials) {
      checkExistingCredentials();
    }
  }, [open, existingCredentials]);

  const checkExistingCredentials = async () => {
    try {
      const data = await whatsappApi.getStatus(smbId);
      if (data.has_credentials) {
        setHasExistingCredentials(true);
        // Try to fetch existing credentials to populate the form
        try {
          const credsData = await whatsappApi.getCredentials(smbId);
          setFormData(prev => ({
            ...prev,
            business_account_id: credsData.business_account_id || '',
            phone_number_id: credsData.phone_number_id || '',
            phone_number: credsData.phone_number || '',
            app_id: credsData.app_id || '',
            // It's okay to pre-fill the webhook token if it exists
            webhook_verify_token: credsData.webhook_verify_token || '',
            // Don't populate sensitive fields
            access_token: '',
            app_secret: '********',
            is_active_assignment: typeof credsData.is_active_assignment === 'boolean' ? credsData.is_active_assignment : true,
          }));
          setAccessOnlyMode(true);
        } catch (error) {
          console.log('Could not fetch existing credentials details');
        }
      }
    } catch (error) {
      console.log('No existing credentials found');
    }
  };

  const handleInputChange = (field: keyof WhatsAppCredentials, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear test result when credentials change
    if (['access_token', 'app_secret', 'business_account_id'].includes(field)) {
      setTestResult({ status: null, message: '' });
    }
  };

  const toggleTokenVisibility = (field: 'access_token' | 'app_secret') => {
    setShowTokens(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const loadTestCredentials = () => {
    const testCreds = { ...getTestCredentials(), is_active_assignment: true };
    setFormData(testCreds);
    showNotification('Test credentials loaded successfully!', 'success');
    setTestResult({ status: null, message: '' });
  };

  const isTestConnectionDisabled =
    testing ||
    (!formData.access_token && !canUseSavedCredentials) ||
    (!formData.business_account_id && !canUseSavedCredentials);

  const toggleAccessOnlyMode = () => {
    setAccessOnlyMode(prev => {
      const next = !prev;
      setFormData(prevForm => ({
        ...prevForm,
        app_secret: next ? '********' : ''
      }));
      return next;
    });
  };

  const buildSubmitPayload = (): Partial<WhatsAppCredentials> => {
    if (!isEditing) {
      return { ...formData };
    }

    const payload: Partial<WhatsAppCredentials> = {};

    if (formData.access_token) {
      payload.access_token = formData.access_token;
    }

    if (
      typeof formData.webhook_verify_token !== 'undefined' &&
      formData.webhook_verify_token !== existingCredentials?.webhook_verify_token
    ) {
      payload.webhook_verify_token = formData.webhook_verify_token || undefined;
    }

    if (!accessOnlyMode) {
      payload.business_account_id = formData.business_account_id;
      payload.phone_number_id = formData.phone_number_id;
      payload.phone_number = formData.phone_number;
      payload.app_id = formData.app_id;
      if (formData.app_secret && formData.app_secret !== '********') {
        payload.app_secret = formData.app_secret;
      }
    }

    if (typeof formData.is_active_assignment === 'boolean') {
      payload.is_active_assignment = formData.is_active_assignment;
    }

    return payload;
  };

  const validateForm = () => {
    // When editing, tokens are not required if the user doesn't want to change them.
    // For testing connection, however, they are.
    const requiredForTest = [
      'business_account_id',
      'phone_number_id', 
      'access_token',
    ];

    // For saving, more fields are required.
    const requiredForSave = [
      'business_account_id',
      'phone_number_id', 
      'phone_number',
      'access_token',
      'app_id',
      'app_secret'
    ];
    
    // Use different required fields for test vs save
    const fieldsToCheck = testing ? requiredForTest : requiredForSave;

    for (const field of fieldsToCheck) {
      if (!isEditing && !formData[field as keyof WhatsAppCredentials]) {
        showNotification(`${field.replace('_', ' ')} is required`, 'error');
        return false;
      }
    }

    // Phone number format validation
    const phoneNumberValue = formData.phone_number || '';
    if (!phoneNumberValue.startsWith('+')) {
      showNotification('Phone number must include country code (start with +)', 'error');
      return false;
    }

    return true;
  };

  const testConnection = async () => {
    const usingSavedCredentials = canUseSavedCredentials;

    if (!formData.access_token && !usingSavedCredentials) {
      showNotification('Add an access token or rely on previously saved credentials to run the test.', 'error');
      return;
    }

    if (!formData.business_account_id && !usingSavedCredentials) {
      showNotification('Business Account ID is required when testing new credentials.', 'error');
      return;
    }

    setTesting(true);
    setTestResult({ status: null, message: '' });

    try {
      const response = await whatsappApi.testConnection(smbId, {
        business_account_id: formData.business_account_id || undefined,
        access_token: formData.access_token || undefined,
        use_saved_credentials: usingSavedCredentials,
        phone_number_id: formData.phone_number_id, // Optional but good to include
        app_id: formData.app_id, // Optional
        app_secret: formData.app_secret, // Optional
      });

      const data = response;

      const success = data.connection_status === 'success';
      const sourceLabel = data.token_source === 'saved' ? 'saved credentials' : 'provided credentials';

      if (success) {
        setTestResult({
          status: 'success',
          message: `Connection successful using ${sourceLabel}! Business: ${data.business_account_info?.name || 'Unknown'}`
        });
        showNotification('WhatsApp connection test successful!', 'success');
        window.dispatchEvent(new CustomEvent('whatsapp-credentials-updated', { detail: { smbId } }));
      } else {
        setTestResult({
          status: 'error',
          message: `${data.message || 'Connection test failed'} (using ${sourceLabel})`
        });
        showNotification('Connection test failed', 'error');
      }
    } catch (error) {
      setTestResult({
        status: 'error',
        message: 'Network error during connection test'
      });
      showNotification('Network error during connection test', 'error');
    } finally {
      setTesting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return; // This will use the save-time validation rules

    setLoading(true);

    try {
      const method = isEditing ? 'PUT' : 'POST';
      const submitData = buildSubmitPayload();

      if (isEditing && Object.keys(submitData).length === 0) {
        showNotification('No changes to update', 'info');
        setLoading(false);
        return;
      }

      try {
        await whatsappApi.saveCredentials(smbId, submitData, method);
      } catch (error: any) {
        // If POST fails with 409 (conflict), try PUT instead
        if (error.response?.status === 409 && method === 'POST') {
          console.log('Credentials already exist, retrying with PUT');
          await whatsappApi.saveCredentials(smbId, submitData, 'PUT');
          
          showNotification('Credentials updated successfully!', 'success');
          onSuccess?.();
          onOpenChange(false);
          return;
        }
        throw error;
      }

      showNotification(isEditing ? 'Credentials updated successfully!' : 'Credentials added successfully!', 'success');
      onSuccess?.();
      window.dispatchEvent(new CustomEvent('whatsapp-credentials-updated', { detail: { smbId } }));
      onOpenChange(false);

    } catch (error) {
      console.error('Error saving credentials:', error);
      showNotification(error instanceof Error ? error.message : 'Failed to save credentials', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={() => onOpenChange(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WhatsAppIcon color="primary" />
          {isEditing ? 'Update WhatsApp Credentials' : 'Add WhatsApp Credentials'}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Configure WhatsApp Business API credentials to enable messaging integration.
          {!isEditing && ' All fields are required except webhook verify token.'}
        </Typography>
        {isEditing && (
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="text"
              size="small"
              onClick={toggleAccessOnlyMode}
              sx={{ textTransform: 'none', px: 0 }}
            >
              {accessOnlyMode ? 'Unlock advanced fields' : 'Lock advanced fields'}
            </Button>
            {accessOnlyMode && (
              <Typography variant="caption" color="text.secondary">
                Updating only the access token. Unlock to modify other credentials.
              </Typography>
            )}
          </Box>
        )}
        {isDevelopment() && !isEditing && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              size="small"
              color="secondary"
              onClick={loadTestCredentials}
              sx={{ 
                fontSize: '0.75rem',
                textTransform: 'none',
                borderColor: 'orange',
                color: 'orange',
                '&:hover': {
                  borderColor: 'darkorange',
                  backgroundColor: 'rgba(255, 165, 0, 0.04)'
                }
              }}
            >
              🧪 Load Test Credentials (Dev Only)
            </Button>
          </Box>
        )}
      </DialogTitle>

        <DialogContent>
          <form onSubmit={handleSubmit}>
            {/* Business Account Information */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'medium' }}>
                Business Account Information
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Business Account ID"
                    value={formData.business_account_id}
                    onChange={(e) => handleInputChange('business_account_id', e.target.value)}
                    placeholder="123456789012345"
                    disabled={loading || (isEditing && accessOnlyMode)}
                    required
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone Number ID"
                    value={formData.phone_number_id}
                    onChange={(e) => handleInputChange('phone_number_id', e.target.value)}
                    placeholder="123456789012345"
                    disabled={loading || (isEditing && accessOnlyMode)}
                    required
                  />
                </Grid>
              </Grid>

              <TextField
                fullWidth
                label="WhatsApp Phone Number"
                value={formData.phone_number}
                onChange={(e) => handleInputChange('phone_number', e.target.value)}
                placeholder="+1234567890"
                disabled={loading || (isEditing && accessOnlyMode)}
                required
                sx={{ mb: 2 }}
              />

              <Tooltip
                title={isEditing ? 'Only one SMB can be active per WhatsApp number. Enabling this will deactivate the others automatically.' : 'Each WhatsApp number can only route to one SMB at a time.'}
                placement="top"
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(formData.is_active_assignment)}
                      onChange={(_e, checked) => handleInputChange('is_active_assignment', checked)}
                      color="primary"
                      disabled={loading}
                    />
                  }
                  label={Boolean(formData.is_active_assignment) ? 'Route replies to this SMB' : 'This SMB is inactive for this WhatsApp number'}
                />
              </Tooltip>
            </Box>

            {/* API Credentials */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'medium' }}>
                API Credentials
              </Typography>
              
              <TextField
                fullWidth
                label="App ID"
                value={formData.app_id}
                onChange={(e) => handleInputChange('app_id', e.target.value)}
                placeholder="123456789012345"
                disabled={loading || (isEditing && accessOnlyMode)}
                required
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label={`Access Token ${isEditing ? '(leave empty to keep current)' : ''}`}
                type={showTokens.access_token ? 'text' : 'password'}
                value={formData.access_token}
                onChange={(e) => handleInputChange('access_token', e.target.value)}
                placeholder={isEditing ? 'Enter new token to update' : 'EAA...'}
                disabled={loading}
                required={!isEditing}
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => toggleTokenVisibility('access_token')}
                        edge="end"
                      >
                        {showTokens.access_token ? <EyeOff /> : <Eye />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label={isEditing && accessOnlyMode ? 'App Secret (locked)' : 'App Secret'}
                type={showTokens.app_secret ? 'text' : 'password'}
                value={formData.app_secret}
                onChange={(e) => handleInputChange('app_secret', e.target.value)}
                placeholder={isEditing ? 'Locked (unlock advanced fields to update)' : 'abc...'}
                disabled={loading || (isEditing && accessOnlyMode)}
                required={!isEditing}
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => toggleTokenVisibility('app_secret')}
                        edge="end"
                        disabled={isEditing && accessOnlyMode}
                      >
                        {showTokens.app_secret ? <EyeOff /> : <Eye />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Webhook Verify Token (Optional)"
                value={formData.webhook_verify_token}
                onChange={(e) => handleInputChange('webhook_verify_token', e.target.value)}
                placeholder="your-webhook-verify-token"
                disabled={loading || (isEditing && accessOnlyMode)}
                sx={{ mb: 2 }}
              />
            </Box>

            {/* Test Connection */}
            <Box sx={{ mb: 2 }}>
              <Button
                type="button"
                variant="outlined"
                onClick={testConnection}
                disabled={isTestConnectionDisabled}
                startIcon={testing ? <CircularProgress size={16} /> : undefined}
                sx={{ mb: 2 }}
              >
                Test Connection
              </Button>

              {testResult.status && (
                <Alert 
                  severity={testResult.status}
                  icon={testResult.status === 'success' ? <CheckCircle2 /> : <AlertCircle />}
                >
                  {testResult.message}
                </Alert>
              )}
            </Box>
          </form>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} /> : undefined}
          >
            {isEditing ? 'Update Credentials' : 'Save Credentials'}
          </Button>
        </DialogActions>
    </Dialog>
  );
}
